import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  sendEmailVerification,
  User as FirebaseUser,
  signInWithRedirect,
  signInWithPopup,
  GoogleAuthProvider,
  EmailAuthProvider,
  RecaptchaVerifier,
  linkWithPhoneNumber,
  ConfirmationResult,
  linkWithCredential,
  reauthenticateWithCredential,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
} from 'firebase/auth';

import { auth } from '@config/firebase';
import { API_ROUTES, APP_ROUTES } from '@config/routes';
import api from './api';
import type { LoginFormData } from '@/types';

const getEmailVerificationSettings = () => {
  const baseUrl = window.location.origin;
  return {
    // Return users to the active onboarding flow after clicking the verification link.
    url: `${baseUrl}${APP_ROUTES.ONBOARDING}`,
    handleCodeInApp: true,
  };
};

let phoneRecaptchaVerifier: RecaptchaVerifier | null = null;
let phoneConfirmationResult: ConfirmationResult | null = null;

export const authService = {
  // Firebase Authentication
  login: async (credentials: LoginFormData): Promise<FirebaseUser> => {
    // Choose persistence based on "Remember me" checkbox
    const remember = !!credentials.rememberMe;
    try {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
    } catch (e) {
      console.warn('[authService] Failed to set persistence, proceeding with default', e);
    }

    const userCredential = await signInWithEmailAndPassword(
      auth,
      credentials.email,
      credentials.password
    );

    // Track whether user chose a persistent session so other flows (onboarding templates) can behave conservatively
    if (remember) {
      try {
        localStorage.setItem('drahft_persistent_session', 'true');
      } catch (e) {
        /* ignore */
      }
    } else {
      try {
        localStorage.removeItem('drahft_persistent_session');
      } catch (e) {
        /* ignore */
      }
    }

    return userCredential.user;
  },

  register: async (): Promise<FirebaseUser> => {
    throw Object.assign(
      new Error('Only Google signups are allowed. Please continue with Google.'),
      {
        code: 'auth/google-signup-required',
      }
    );
  },

  logout: async (): Promise<void> => {
    try {
      const { unregisterBrowserPushNotifications } = await import('./browserPushService');
      await unregisterBrowserPushNotifications();
    } catch (e) {
      console.error('[authService] Failed to unregister browser push notifications', e);
    }

    await signOut(auth);

    // Clear known sensitive localStorage and sessionStorage keys to avoid reuse on shared devices
    const keysToClear = [
      'googleAuth',
      'registeredEmail',
      'drahft_google_token',
      'drahft_calendar_email',
      'drahft_session_id',
      'drahft_conversation_id',
      'drahft_chat_history',
      'drahft_all_histories',
      'onboardingDraft',
      'training_autosave',
      'training_selected_section',
      'drahft_email_settings',
      'drahft_calendar_settings',
      'drahft_persistent_session',
    ];

    try {
      keysToClear.forEach(k => {
        try {
          localStorage.removeItem(k);
        } catch (e) {
          // ignore
        }
        try {
          sessionStorage.removeItem(k);
        } catch (e) {
          // ignore
        }
      });
    } catch (e) {
      // ignore
    }
  },

  forgotPassword: async (email: string): Promise<void> => {
    await sendPasswordResetEmail(auth, email);
  },

  /** Check whether the current user has an email/password provider linked */
  hasPasswordProvider: (): boolean => {
    const user = auth.currentUser;
    return user?.providerData.some(p => p.providerId === 'password') ?? false;
  },

  /**
   * Set or update the user's password.
   * - Google-only users: links an email/password credential (no current password needed).
   * - Users who already have password auth: requires currentPassword for reauthentication.
   */
  setOrUpdatePassword: async (newPassword: string, currentPassword?: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    if (!user.email) throw new Error('No email address on this account');
    if (newPassword.length < 6) throw new Error('Password must be at least 6 characters');

    const hasPassword = user.providerData.some(p => p.providerId === 'password');

    if (hasPassword) {
      // Existing password user — reauthenticate first, then update
      if (!currentPassword) throw new Error('Current password is required');
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      try {
        await reauthenticateWithCredential(user, credential);
      } catch (err: any) {
        if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
          throw new Error('Current password is incorrect');
        }
        throw err;
      }
      await updatePassword(user, newPassword);
    } else {
      // Google-only user — link email/password provider
      const credential = EmailAuthProvider.credential(user.email, newPassword);
      try {
        await linkWithCredential(user, credential);
      } catch (err: any) {
        if (err?.code === 'auth/provider-already-linked') {
          // Race condition — provider was just linked; fall back to updatePassword
          await updatePassword(user, newPassword);
        } else if (err?.code === 'auth/requires-recent-login') {
          throw new Error('For security, please sign out and sign back in, then try again.');
        } else {
          throw err;
        }
      }
    }

    // Reload to pick up provider changes
    await user.reload();
  },

  updateUserPassword: async (newPassword: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    await updatePassword(user, newPassword);
  },

  updateUserProfile: async (data: { displayName?: string; photoURL?: string }): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    await updateProfile(user, data);
  },

  sendVerificationEmail: async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    await sendEmailVerification(user, getEmailVerificationSettings());
  },

  sendEmailVerificationCode: async (): Promise<void> => {
    await api.post(API_ROUTES.USERS.EMAIL_VERIFICATION_SEND, {});
  },

  verifyEmailVerificationCode: async (code: string): Promise<void> => {
    await api.post(API_ROUTES.USERS.EMAIL_VERIFICATION_VERIFY, { code });
    const user = auth.currentUser;
    if (user) {
      await user.reload();
    }
  },

  // Get current user token
  getCurrentToken: async (): Promise<string | null> => {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  },

  // Google Sign-In - Smart flow selection
  signInWithGoogle: async (
    remember = false
  ): Promise<{ user: FirebaseUser; isNewUser: boolean } | void> => {
    const provider = new GoogleAuthProvider();

    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const shouldUseRedirect = !isLocalhost;

    // Set persistence based on remember flag
    try {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      if (remember) {
        localStorage.setItem('drahft_persistent_session', 'true');
      } else {
        localStorage.removeItem('drahft_persistent_session');
      }
    } catch (e) {
      console.warn('[authService] Failed to set persistence for Google sign-in', e);
    }

    console.log('[authService] Starting Google sign-in...');
    console.log(
      '[authService] Environment:',
      shouldUseRedirect ? 'production (redirect)' : 'localhost (popup)'
    );

    if (!shouldUseRedirect) {
      try {
        console.log('[authService] Opening Google popup...');
        const result = await signInWithPopup(auth, provider);

        console.log('[authService] ✅ Popup sign-in successful');
        console.log('[authService] User UID:', result.user.uid);
        console.log('[authService] User email:', result.user.email);

        const { user } = result;

        // Store Google auth info (transient flag)
        sessionStorage.setItem('googleAuth', 'true');

        // Determine if this is a new user from Firebase metadata only —
        // backend profile creation is deferred until the user explicitly accepts Terms.
        const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
        console.log('[authService] Popup sign-in complete; isNewUser:', isNewUser);

        console.log('[authService] ✅ Returning user data');
        return { user, isNewUser };
      } catch (error: unknown) {
        console.error('[authService] ❌ Popup sign-in error:', error);
        const code = (error as { code?: string } | undefined)?.code;
        const message = (error as Error | undefined)?.message;
        console.error('[authService] Error code:', code);
        console.error('[authService] Error message:', message);

        // Handle popup errors
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          console.log('[authService] User cancelled popup');
          throw new Error('Sign-in cancelled');
        }

        if (code === 'auth/popup-blocked') {
          console.error('[authService] Popup was blocked by browser');
          throw new Error('Popup blocked. Please allow popups for this site.');
        }

        throw error;
      }
    } else {
      // Production: Use redirect
      console.log('[authService] Redirecting to Google...');
      await signInWithRedirect(auth, provider);
      // Explicitly return undefined to satisfy consistent-return rule
      return undefined;
    }
  },

  sendPhoneVerificationCode: async (
    phoneNumber: string,
    recaptchaContainerId = 'phone-recaptcha-container'
  ): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');

    if (phoneRecaptchaVerifier) {
      phoneRecaptchaVerifier.clear();
      phoneRecaptchaVerifier = null;
    }

    phoneRecaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
      size: 'invisible',
    });

    phoneConfirmationResult = await linkWithPhoneNumber(user, phoneNumber, phoneRecaptchaVerifier);
  },

  confirmPhoneVerificationCode: async (code: string): Promise<string> => {
    if (!phoneConfirmationResult) {
      throw new Error('Verification session expired. Please request a new code.');
    }

    const result = await phoneConfirmationResult.confirm(code);
    phoneConfirmationResult = null;

    if (phoneRecaptchaVerifier) {
      phoneRecaptchaVerifier.clear();
      phoneRecaptchaVerifier = null;
    }

    await result.user.reload();
    return result.user.phoneNumber || '';
  },

  hasVerifiedPhone: (): boolean => {
    return !!auth.currentUser?.phoneNumber;
  },

  // Create backend profile for the currently signed-in user (called after Terms accepted)
  createBackendProfileForCurrentUser: async (
    acceptedTerms = false,
    extra: Record<string, any> = {}
  ) => {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user to create profile for');

    console.log('[authService] Creating backend profile for current user:', user.email);
    const payload = (await api.post(
      API_ROUTES.USERS.BASE,
      {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        acceptedTerms,
        ...extra,
      },
      { raw: true }
    )) as { success?: boolean; data?: any; existed?: boolean };

    return payload;
  },

  // Delete the currently authenticated Firebase user (used when onboarding is cancelled)
  deleteCurrentUser: async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await import('firebase/auth').then(({ deleteUser }) => deleteUser(user));
      console.log('[authService] Firebase user deleted successfully');
    } catch (err) {
      console.error('[authService] Failed to delete Firebase user:', err);
      // fallback to logout to at least clear session
      await signOut(auth);
    }
  },
};
